/**
 * Everything the Wall shows, and how it stays current.
 *
 * A paired TV is a public screen, so its stream carries signals only — `{ id, type, siteId }`, a
 * `resync`, a ping — and never a figure (docs/api/v1.md, "The TV's stream"). Every number the Wall
 * draws comes from `GET /wall/snapshot`, which the server masks for open counts. So the Wall patches
 * nothing from the stream: a signal only means "something changed, ask again".
 *
 *  - A signal refetches the snapshot, at most once a second and trailing: a pallet of boxes is one
 *    event per box, and a burst of fifty makes one fetch once it has passed.
 *  - `resync` refetches at once: the server is saying a count started or finished, or that the
 *    stream has a gap.
 *  - The stream opens from the snapshot's `lastEventId`, so a count that commits between the
 *    snapshot and the subscription still reaches the TV.
 *  - A 401 from the stream or from any refetch means this TV is no longer paired: back to pairing.
 *  - Down for more than 5 s: the reconnecting bar. More than 30 s: the snapshot is fetched every
 *    20 s until the stream is back, since nothing else would move the numbers.
 */
import type { WallSnapshotDto } from '@pl/domain';
import { useCallback, useEffect, useRef, useState } from 'react';
import { openStream, type StreamStatus } from '../lib/stream';

export const SIGNAL_DEBOUNCE_MS = 1000;
export const RECONNECT_BAR_AFTER_MS = 5000;
export const POLL_AFTER_MS = 30_000;
export const POLL_EVERY_MS = 20_000;

export type SnapshotResult = { ok: true; snapshot: WallSnapshotDto } | { ok: false; status: number | null; code?: string; message?: string };

/**
 * A board. A TV sends no `siteId`: its device cookie decides the site on the server. A signed-in
 * person previewing the Wall must say which site, and is answered 400 without one.
 */
export async function fetchSnapshot(siteId?: string): Promise<SnapshotResult> {
  try {
    const url = siteId ? `/api/v1/wall/snapshot?siteId=${encodeURIComponent(siteId)}` : '/api/v1/wall/snapshot';
    const res = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
    if (!res.ok) return { ok: false, status: res.status, ...(await errorOf(res)) };
    return { ok: true, snapshot: (await res.json()) as WallSnapshotDto };
  } catch {
    return { ok: false, status: null }; // no answer: the network or the server is down
  }
}

/** The API's `{ error: { code, message } }`, or nothing when a proxy answered instead. */
async function errorOf(res: Response): Promise<{ code?: string; message?: string }> {
  try {
    const body = (await res.json()) as { error?: { code?: unknown; message?: unknown } } | null;
    const e = body?.error;
    return { code: typeof e?.code === 'string' ? e.code : undefined, message: typeof e?.message === 'string' ? e.message : undefined };
  } catch {
    return {};
  }
}

export interface WallData {
  snapshot: WallSnapshotDto;
  /** When the snapshot on screen was taken, as the TV's clock saw it arrive. */
  receivedAt: number;
  status: StreamStatus;
  /** The stream has been down longer than `RECONNECT_BAR_AFTER_MS`. */
  showReconnecting: boolean;
}

/** `siteId` is for a signed-in person's preview; a TV leaves it out (see `fetchSnapshot`). */
export function useWallData(initial: WallSnapshotDto, onUnauthorized: () => void, EventSourceImpl?: typeof EventSource, siteId?: string): WallData {
  const [snapshot, setSnapshot] = useState(initial);
  const [receivedAt, setReceivedAt] = useState(() => Date.now());
  const [status, setStatus] = useState<StreamStatus>('connecting');
  const [showReconnecting, setShowReconnecting] = useState(false);
  const onUnauthorizedRef = useRef(onUnauthorized);
  onUnauthorizedRef.current = onUnauthorized;
  // The first position only: the stream keeps its own after that.
  const startFrom = useRef(initial.lastEventId);

  useEffect(() => {
    let closed = false;
    let inFlight = false;
    let again = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const run = async () => {
      if (inFlight) {
        again = true;
        return;
      }
      inFlight = true;
      const r = await fetchSnapshot(siteId);
      inFlight = false;
      if (closed) return;
      if (r.ok) {
        setSnapshot(r.snapshot);
        setReceivedAt(Date.now());
      } else if (r.status === 401) {
        onUnauthorizedRef.current();
        return;
      }
      // Anything else keeps what is on screen; the next signal or poll asks again.
      if (again) {
        again = false;
        schedule(SIGNAL_DEBOUNCE_MS);
      }
    };

    /** At most one fetch per window, trailing: a signal that finds one already waiting joins it. */
    function schedule(delayMs: number) {
      if (closed || timer) return;
      timer = setTimeout(() => {
        timer = null;
        void run();
      }, delayMs);
    }

    /** A fetch now, taking the place of any that was waiting: for a resync, and for each poll. */
    function fetchNow() {
      if (closed) return;
      if (timer) clearTimeout(timer);
      timer = null;
      void run();
    }

    let dropped = false;
    let barTimer: ReturnType<typeof setTimeout> | null = null;
    let pollStart: ReturnType<typeof setTimeout> | null = null;
    let poll: ReturnType<typeof setInterval> | null = null;
    const clearDown = () => {
      if (barTimer) clearTimeout(barTimer);
      if (pollStart) clearTimeout(pollStart);
      if (poll) clearInterval(poll);
      barTimer = pollStart = poll = null;
    };

    const close = openStream({
      site: siteId ?? 'all', // a TV's cookie pins it to its own site whatever it asks for
      lastEventId: startFrom.current,
      EventSourceImpl,
      onMessage: (kind) => (kind === 'resync' ? fetchNow() : schedule(SIGNAL_DEBOUNCE_MS)),
      onStatus: (s) => {
        setStatus(s);
        if (s === 'reconnecting' && !dropped) {
          dropped = true;
          barTimer = setTimeout(() => setShowReconnecting(true), RECONNECT_BAR_AFTER_MS);
          pollStart = setTimeout(() => {
            fetchNow();
            poll = setInterval(fetchNow, POLL_EVERY_MS);
          }, POLL_AFTER_MS);
        }
        if (s === 'live' && dropped) {
          dropped = false;
          clearDown();
          setShowReconnecting(false);
          // Replayed signals will ask again too; this covers a stream that came back with none.
          schedule(SIGNAL_DEBOUNCE_MS);
        }
      },
      onUnauthorized: () => onUnauthorizedRef.current(),
    });

    return () => {
      closed = true;
      close();
      clearDown();
      if (timer) clearTimeout(timer);
    };
  }, [EventSourceImpl, siteId]);

  return { snapshot, receivedAt, status, showReconnecting };
}

/** Ticks every `ms`, for the clock and the countdowns. */
export function useNow(ms = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

/**
 * The parts of an instant on a site's wall clock, the hour 0-23. `hour12: false` rather than
 * `hourCycle`: Chrome before 73 and Safari before 13 ignore `hourCycle`, fall back to 12-hour time
 * for en-US, and "03" would then be 3 PM as often as 3 AM. Some engines write midnight as "24"
 * under `hour12: false`, hence the modulo.
 */
function wallParts(at: number, timeZone: string): { hour: number; minute: number; second: number } {
  const opts: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false };
  let f: Intl.DateTimeFormat;
  try {
    f = new Intl.DateTimeFormat('en-US', { ...opts, timeZone });
  } catch {
    // A zone newer than this TV's own zone data: the site's clock read as UTC, as the header does.
    f = new Intl.DateTimeFormat('en-US', { ...opts, timeZone: 'UTC' });
  }
  const parts = f.formatToParts(at);
  const n = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return { hour: n('hour') % 24, minute: n('minute'), second: n('second') };
}

/**
 * Milliseconds until the next 03:00 on the site's clock. Worked out from the site's own wall-clock
 * reading, then corrected by what that clock actually says at the result, so a daylight-saving night
 * still lands on 03:00 (or on the first minute after it, on the night 03:00 happens twice).
 */
export function msUntilNext3am(now: number, timeZone: string): number {
  const p = wallParts(now, timeZone);
  const secondsIn = p.hour * 3600 + p.minute * 60 + p.second;
  let delta = (3 * 3600 - secondsIn) * 1000;
  if (delta <= 0) delta += 24 * 3600 * 1000;
  for (let i = 0; i < 2; i++) {
    const q = wallParts(now + delta, timeZone);
    const off = (q.hour - 3) * 3600 + q.minute * 60 + q.second;
    if (off === 0) break;
    // An hour gained or lost on the way: move back to 03:00 by what the clock says.
    delta -= (off > 12 * 3600 ? off - 24 * 3600 : off) * 1000;
  }
  return delta;
}

/** Reloads the page at the next 03:00 on the site's clock, the Wall's nightly fresh start. */
export function useNightlyReload(timeZone: string, reload: () => void): void {
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  useEffect(() => {
    let wait: number;
    try {
      wait = msUntilNext3am(Date.now(), timeZone);
    } catch {
      // Below the Chrome 57 floor there is no formatToParts: reload a day from now instead.
      wait = 24 * 3600_000;
    }
    const t = setTimeout(() => reloadRef.current(), wait);
    return () => clearTimeout(t);
  }, [timeZone]);
}

/** Stable callback wrapper, for handlers passed down to effects. */
export function useEvent<A extends unknown[]>(fn: (...a: A) => void): (...a: A) => void {
  const ref = useRef(fn);
  ref.current = fn;
  return useCallback((...a: A) => ref.current(...a), []);
}
